import api from '@/services/api'
import store from '@/store/index'

import Auth from '../auth/index'
import { apiErrorHandler } from '../utils/apiErrors'

const PROJECT_ENDPOINT = 'projects/'
const MAKER_PROJECT_FEED = 'projects/maker-feed/'

export default class ProjectAPI {
  static UPLOAD_IMAGE_ENDPOINT = id => `projects/${id}/profile_pic/`

  /**
   * Instantiate a new `projectAPI`
   * @param {class} cls - The class to use to create objects.
   */
  constructor(cls) {
    this.cls = cls
  }

  /**
   * Factory method to create a new instance of `projectAPI`.
   * @param {class} cls - The class to use to create objects.
   **/
  static create(cls) {
    return new ProjectAPI(cls)
  }

  /* Get the API Client. */
  get client() {
    return api.getApiClient()
  }

  /**
   * Retrieve one project from the API.
   *
   * @param {String} projectId  Unique ID of the project to retrieve.
   */
  get(projectId) {
    const url = `${PROJECT_ENDPOINT}${projectId}/`
    return this.client.get(url).then(response => this.cls.fromAPI(response.data))
  }

  /**
   * Create a new Project through the API.
   * @param {Event} project - Data for the project to be created.
   * @returns {Promise} - Promise wrapping the newly-created `Project` data.
   **/
  async create(project) {
    const url = PROJECT_ENDPOINT
    const data = this.cls.toAPI(project)

    return this.client
      .post(url, data)
      .then(response => response.data)
      .then(this.cls.fromAPI)
      .catch(apiErrorHandler({ apiName: 'Create project API error' }))
  }

  /**
   * Get List of projects via the API
   *
   * @returns {Promise} - Promise wrapping the API response data
   */
  async list({ filters = {} } = {}) {
    const authHeader = Auth.getAuthorizationHeader()
    const options = {
      headers: {
        ...authHeader,
      },
      params: {},
    }
    if (filters.page) {
      options.params.page = filters.page
    }
    if (filters.ordering) {
      options.params.ordering = filters.ordering
    }
    if (filters.search) {
      options.params.search = filters.search
    }
    if (filters.attributes && filters.attributes.length > 0) {
      options.params.attributes = filters.attributes.join(',')
    }
    return this.client
      .get(PROJECT_ENDPOINT, options)
      .then(response => {
        return {
          ...response.data,
          results: response.data.results.map(this.cls.fromAPI),
        }
      })
      .catch(apiErrorHandler({ apiName: 'List Projects API error' }))
  }

  /**
   * Retrieve a single project from the API
   * @param {string} projectId - ID of the event to retrieve.
   * @returns {Promise} - Promise wrapping the retrieved `project` instance.
   */
  async retrieve(projectId) {
    const url = `${PROJECT_ENDPOINT}${projectId}/`
    return this.client.get(url).then(response => this.cls.fromAPI(response.data))
  }

  /**
   * Update an project through the API.
   *
   * @param {project} project - The event to update.
   * @param {Array} fields - List of fields to include in update, others will be ignored.
   * @param {Array} excludeFields - List of fields to exclude from update.
   *
   * @returns {Promise} - Promise object wrapping API response data.
   **/
  async update(project, fields = []) {
    this.cls.checkIsInstance(project, 'The `project` argument')

    const url = `${PROJECT_ENDPOINT}${project.id}/`
    const data = this.cls.toAPI(project, fields)

    const promise = this.client.patch(url, data)
    return promise
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'Update project API error' }))
  }

  /**
   * Retrieve a Maker's project feed.
   *
   * @returns {Promise} - Promise wrapping the API response data
   */
  async getMakerProjectFeed({ filters = {} } = {}) {
    const authHeader = Auth.getAuthorizationHeader()
    const options = {
      headers: {
        ...authHeader,
      },
      params: {},
    }
    if (filters.page) {
      options.params.page = filters.page
    }
    return this.client
      .get(MAKER_PROJECT_FEED, options)
      .then(response => {
        return {
          ...response.data,
          results: response.data.results.map(this.cls.fromAPI),
        }
      })
      .catch(apiErrorHandler({ apiName: 'Get Maker Project Feed API error' }))
  }

  /**
   * Upload project's profile image via the API.
   * @param {File} imageFile - project's imageFile to be uploaded.
   * @returns {Promise} - Contains the API response data.
   */

  async uploadProfilePic(projectId, file) {
    const url = `${PROJECT_ENDPOINT}${projectId}/profile-pic/`
    const options = {
      headers: {
        ...store.getters.getHeaders,
        'Content-Type': 'multipart/form-data',
      },
    }
    /* eslint-disable-next-line */
    const data = new FormData()
    data.append('profile_pic', file)

    return this.client
      .post(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'Upload project Profile Pic API error' }))
  }

  async uploadImage(projectId, file) {
    const url = `${PROJECT_ENDPOINT}${projectId}/profile_pic/`
    const options = {
      headers: {
        ...store.getters.getHeaders,
        'Content-Type': 'multipart/form-data',
      },
    }
    const data = new FormData()
    data.append('profile_pic', file)
    return this.client.post(url, data, options).catch(error => {
      throw error
    })
  }

  /**
   * Approve Project (NOTE: Only activators can approve projects)
   *
   * @returns {Promise} - Promise wrapping the API response data
   */

  approveProject(projectId) {
    const url = `${PROJECT_ENDPOINT}${projectId}/approve/`
    const authHeader = Auth.getAuthorizationHeader()
    // TODO: All of these should be pulled out to the apiClient in the store
    const options = {
      headers: {
        ...authHeader,
      },
    }
    return this.client
      .post(url, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'Approve Project API error' }))
  }
}
